import {
  PRODUCT_LAW_KEYS,
  type ProductApplicabilityState,
  type ProductLawApplicability,
  type ProductLawApplicabilityStatus,
  type ProductLawKey,
} from '@/shared/hooks/api/product'

const REGULATIONS_SUMMARY_STATUS_ORDER: Record<
  ProductLawApplicabilityStatus,
  number
> = {
  NEEDS_GRILLING: 0,
  APPLICABLE: 1,
  NOT_APPLICABLE: 2,
  UNDECIDED: 3,
}

export function applicabilityStatusCardClassName(
  status: ProductLawApplicabilityStatus
): string {
  switch (status) {
    case 'UNDECIDED':
      return 'border-muted bg-muted/40'
    case 'NEEDS_GRILLING':
      return 'border-amber-300 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/30'
    case 'APPLICABLE':
      return 'border-emerald-200 bg-emerald-50 dark:border-emerald-800 dark:bg-emerald-950/40'
    case 'NOT_APPLICABLE':
      return 'border-border bg-muted/30'
  }
}

function regulationsSummaryStatusRank(
  status: ProductLawApplicabilityStatus | null | undefined
): number {
  if (status == null) {
    return REGULATIONS_SUMMARY_STATUS_ORDER.UNDECIDED
  }

  return (
    REGULATIONS_SUMMARY_STATUS_ORDER[status] ??
    REGULATIONS_SUMMARY_STATUS_ORDER.UNDECIDED
  )
}

export function hasLawApplicabilityRecord(
  applicability: ProductLawApplicability | null | undefined
): boolean {
  if (applicability == null) {
    return false
  }

  if (applicability.source != null) {
    return true
  }

  return applicability.status !== 'UNDECIDED'
}

export function countAssessedLaws(
  applicability: ProductApplicabilityState | null | undefined
): number {
  if (applicability == null) {
    return 0
  }

  return PRODUCT_LAW_KEYS.filter((law) =>
    hasLawApplicabilityRecord(applicability[law])
  ).length
}

export function isProductApplicabilityDiscoverComplete(
  applicability: ProductApplicabilityState | null | undefined
): boolean {
  return countAssessedLaws(applicability) === PRODUCT_LAW_KEYS.length
}

export function sortLawsForRegulationsSummary(
  laws: readonly ProductLawKey[],
  applicability: ProductApplicabilityState,
  getLawLabel: (law: ProductLawKey) => string,
  locale?: string
): ProductLawKey[] {
  return [...laws].sort((lawA, lawB) => {
    const rankA = regulationsSummaryStatusRank(applicability[lawA]?.status)
    const rankB = regulationsSummaryStatusRank(applicability[lawB]?.status)
    if (rankA !== rankB) {
      return rankA - rankB
    }

    return getLawLabel(lawA).localeCompare(getLawLabel(lawB), locale, {
      sensitivity: 'base',
      numeric: true,
    })
  })
}
